import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    function handleScroll() {
      setScrolled(window.scrollY > 50);
    }
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50 border-b border-background-200"
          : "bg-background-50/95 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="text-lg font-extrabold text-foreground-900 font-heading">acbuyspreadsheet<span className="text-accent-500">.blog</span></span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="/"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </a>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              <span>Shopping Guide</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200 rounded-xl shadow-lg animate-fade-in overflow-hidden z-50">
                <div className="max-h-80 overflow-y-auto py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className="font-medium">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-accent-300 text-accent-600 text-sm font-semibold hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            acbuy spreadsheet
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-500 text-background-50 text-sm font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            buy it now
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-2">
            <a
              href="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Home
            </a>

            <div className="px-3 pt-1 pb-2">
              <p className="text-xs font-bold uppercase tracking-widest text-foreground-400 mb-3">Shopping Guide</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 px-2 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="font-medium leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg border border-accent-300 text-accent-600 text-sm font-semibold hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              acbuy spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-accent-500 text-background-50 text-sm font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}